/**
 * Chat — AppSync transport.
 *
 * Three channels (matches Zoom webinar chat model):
 *   - EVERYONE  — broadcast visible to all roles (HOST/SPEAKER/ATTENDEE)
 *   - PANELISTS — broadcast visible to HOST/SPEAKER only. Attendees
 *                 never see these, enforced server-side in the
 *                 getHostChatHistory query filter.
 *   - DIRECT    — 1:1 between sender and targetUserId. Only the
 *                 sender and target see it.
 *
 * The `channel` + `recipient` state combine into the outgoing message
 * routing:
 *   channel=EVERYONE + recipient=null            → public broadcast
 *   channel=PANELISTS + recipient=null           → host/speaker private
 *   channel=DIRECT + recipient={id,name}         → DM
 *
 * Attendees cannot select PANELISTS — enforced in the panel UI and
 * rejected by the backend if they try anyway.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { generateClient } from "aws-amplify/api";
import { configureAppSync } from "../appsync/config";

export type ChatChannel = "EVERYONE" | "PANELISTS" | "DIRECT";

export interface HostChatMessage {
  id: string;
  webinarId: string;
  senderId: string;
  senderName: string;
  senderEmail: string | null;
  senderRole: "HOST" | "SPEAKER" | "ATTENDEE";
  channel: ChatChannel;
  targetUserId: string | null;
  targetUserName: string | null;
  message: string;
  createdAt: string;
}

export interface ChatParticipant {
  id: string;
  name: string;
  role: string;
}

export interface UseHostChatAppSyncParams {
  webinarId: string;
  userId: string;
  userName: string;
  userEmail?: string;
  role: "HOST" | "SPEAKER" | "ATTENDEE";
}

export interface UseHostChatAppSyncResult {
  messages: HostChatMessage[];
  isConnected: boolean;
  error: string | null;
  /** Currently-selected send channel. */
  channel: ChatChannel;
  setChannel: (c: ChatChannel) => void;
  /** Selected DM target (only meaningful when channel === DIRECT). */
  recipient: ChatParticipant | null;
  setRecipient: (r: ChatParticipant | null) => void;
  /** Derived list of other people the current user can DM. */
  participants: ChatParticipant[];
  sendMessage: (text: string) => Promise<void>;
  clearError: () => void;
}

// ---------- Inline GraphQL documents ----------

const GET_HOST_CHAT_HISTORY = /* GraphQL */ `
  query GetHostChatHistory(
    $webinarId: String!
    $userId: String
    $userRole: String
    $limit: Int
  ) {
    getHostChatHistory(
      webinarId: $webinarId
      userId: $userId
      userRole: $userRole
      limit: $limit
    ) {
      id
      webinarId
      senderId
      senderName
      senderEmail
      senderRole
      channel
      targetUserId
      targetUserName
      message
      createdAt
    }
  }
`;

const SEND_HOST_CHAT_MESSAGE = /* GraphQL */ `
  mutation SendHostChatMessage($input: HostChatMessageInput!) {
    sendHostChatMessage(input: $input) {
      id
      webinarId
      senderId
      senderName
      senderEmail
      senderRole
      channel
      targetUserId
      targetUserName
      message
      createdAt
    }
  }
`;

const ON_HOST_CHAT_MESSAGE = /* GraphQL */ `
  subscription OnHostChatMessage($webinarId: String!) {
    onHostChatMessage(webinarId: $webinarId) {
      id
      webinarId
      senderId
      senderName
      senderEmail
      senderRole
      channel
      targetUserId
      targetUserName
      message
      createdAt
    }
  }
`;

// Normalise channel on inbound messages — legacy rows may have null
// `channel` but a `targetUserId`, which means DIRECT in the old model.
function effectiveChannel(m: any): ChatChannel {
  if (m?.channel === "EVERYONE" || m?.channel === "PANELISTS" || m?.channel === "DIRECT") {
    return m.channel;
  }
  return m?.targetUserId ? "DIRECT" : "EVERYONE";
}

export function useHostChatAppSync(
  params: UseHostChatAppSyncParams,
): UseHostChatAppSyncResult {
  const { webinarId, userId, userName, userEmail, role } = params;
  const isPrivileged = role === "HOST" || role === "SPEAKER";

  const [messages, setMessages] = useState<HostChatMessage[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Default channel:
  //   - HOST / SPEAKER → EVERYONE (public broadcast)
  //   - ATTENDEE       → PANELISTS (private — hosts + speakers only)
  // Attendees can never send to EVERYONE; they can send to PANELISTS
  // or DIRECT-message a specific host/speaker.
  const [channel, setChannel] = useState<ChatChannel>(
    isPrivileged ? "EVERYONE" : "PANELISTS",
  );
  const [recipient, setRecipient] = useState<ChatParticipant | null>(null);
  const seen = useRef<Set<string>>(new Set());

  useEffect(() => {
    configureAppSync();
  }, []);

  const client = useMemo(() => generateClient(), []);

  const merge = useCallback((raw: any) => {
    if (!raw?.id || seen.current.has(raw.id)) return;
    seen.current.add(raw.id);
    const msg: HostChatMessage = {
      ...raw,
      channel: effectiveChannel(raw),
    };
    setMessages((prev) => {
      const next = [...prev, msg];
      next.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
      return next;
    });
  }, []);

  // Load history via GraphQL query — server applies role-aware
  // visibility filter (userRole gates PANELISTS channel).
  useEffect(() => {
    if (!webinarId) return;
    let cancelled = false;
    (async () => {
      try {
        const res: any = await client.graphql({
          query: GET_HOST_CHAT_HISTORY,
          variables: { webinarId, userId, userRole: role, limit: 200 },
        });
        if (cancelled) return;
        const items: HostChatMessage[] = (
          res?.data?.getHostChatHistory || []
        ).map((m: any) => ({ ...m, channel: effectiveChannel(m) }));
        seen.current = new Set(items.map((m) => m.id));
        items.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
        setMessages(items);
      } catch (err: any) {
        if (!cancelled) setError(err?.message || "Failed to load chat history");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [webinarId, userId, role, client]);

  // Subscribe to live updates. AppSync pushes every message for this
  // webinar; client-side `visibleMessages` filters by channel + role.
  useEffect(() => {
    if (!webinarId) return;
    let sub: { unsubscribe: () => void } | null = null;
    try {
      const observable = client.graphql({
        query: ON_HOST_CHAT_MESSAGE,
        variables: { webinarId },
      }) as any;
      sub = observable.subscribe({
        next: ({ data }: any) => {
          setIsConnected(true);
          setError(null);
          if (data?.onHostChatMessage) merge(data.onHostChatMessage);
        },
        error: (err: any) => {
          console.error("[useHostChatAppSync] subscription error", err);
          const msg =
            err?.errors?.[0]?.message ||
            err?.message ||
            (typeof err === "string" ? err : "Subscription error");
          setError(msg);
          setIsConnected(false);
        },
      });
      setIsConnected(true);
    } catch (err: any) {
      setError(err?.message || "Failed to open subscription");
      setIsConnected(false);
    }
    return () => {
      try {
        sub?.unsubscribe();
      } catch {
        /* ignore */
      }
      setIsConnected(false);
    };
  }, [webinarId, client, merge]);

  // Client-side visibility filter — defense in depth for the server-
  // side filter. Role-aware:
  //   - EVERYONE: visible to all
  //   - PANELISTS: visible to hosts/speakers. Attendees only see
  //     their OWN panelists messages (so they can see their "ask
  //     panelists privately" messages after sending, but not the
  //     host/speaker back-and-forth on that channel).
  //   - DIRECT: visible to sender and target only
  const visibleMessages = useMemo(() => {
    return messages.filter((m) => {
      if (m.channel === "EVERYONE") return true;
      if (m.channel === "PANELISTS") {
        if (isPrivileged) return true;
        if (m.senderId === userId) return true;
        if (!!userEmail && m.senderEmail === userEmail) return true;
        return false;
      }
      if (m.channel === "DIRECT") {
        if (m.targetUserId === userId) return true;
        if (m.senderId === userId) return true;
        if (!!userEmail && m.targetUserId === userEmail) return true;
        return false;
      }
      return false;
    });
  }, [messages, userId, userEmail, isPrivileged]);

  // Derived participants list — for privileged users, include everyone
  // who has spoken. For attendees, show only host/speaker senders so
  // they can DM up but not to other attendees.
  const participants = useMemo<ChatParticipant[]>(() => {
    const byId = new Map<string, ChatParticipant>();
    for (const m of messages) {
      if (!m.senderId || m.senderId === userId) continue;
      const senderRole = String(m.senderRole || "").toUpperCase();
      if (!isPrivileged && senderRole !== "HOST" && senderRole !== "SPEAKER") {
        continue; // attendees can only DM hosts/speakers
      }
      byId.set(m.senderId, {
        id: m.senderId,
        name: m.senderName,
        role: senderRole,
      });
    }
    return Array.from(byId.values());
  }, [messages, userId, isPrivileged]);

  // Send — routes through channel + recipient state.
  const sendMessage = useCallback(
    async (text: string) => {
      const message = (text || "").trim();
      if (!message) return;

      // Guards:
      //   - DIRECT requires a recipient
      //   - Attendees can never send to EVERYONE — coerce to PANELISTS
      //     client-side so the outgoing payload matches backend rules.
      if (channel === "DIRECT" && !recipient) {
        setError("Select a recipient for direct messages");
        return;
      }
      const outgoingChannel: ChatChannel =
        !isPrivileged && channel === "EVERYONE" ? "PANELISTS" : channel;

      const targetUserId =
        outgoingChannel === "DIRECT" ? recipient?.id ?? null : null;
      const targetUserName =
        outgoingChannel === "DIRECT" ? recipient?.name ?? null : null;

      try {
        const res: any = await client.graphql({
          query: SEND_HOST_CHAT_MESSAGE,
          variables: {
            input: {
              webinarId,
              senderId: userId,
              senderName: userName,
              senderEmail: userEmail ?? null,
              senderRole: role,
              channel: outgoingChannel,
              targetUserId,
              targetUserName,
              message,
            },
          },
        });
        const saved: any = res?.data?.sendHostChatMessage;
        if (saved) merge(saved);
      } catch (err: any) {
        setError(err?.message || "Failed to send message");
      }
    },
    [
      client,
      webinarId,
      userId,
      userName,
      userEmail,
      role,
      channel,
      recipient,
      isPrivileged,
      merge,
    ],
  );

  const clearError = useCallback(() => setError(null), []);

  return {
    messages: visibleMessages,
    isConnected,
    error,
    channel,
    setChannel,
    recipient,
    setRecipient,
    participants,
    sendMessage,
    clearError,
  };
}
